import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ConnectionBanner from "@/components/layout/ConnectionBanner";
import ForcePasswordChange from "@/components/auth/ForcePasswordChange";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MODULES, canSee } from "@/lib/modules";
import AppLayout from "@/components/layout/AppLayout";
import { FullScreenLoader, Forbidden } from "@/components/common/States";
import Login from "@/pages/Login";
import Privacy from "@/pages/Privacy";
import AccountDeletion from "@/pages/AccountDeletion";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Customers from "@/pages/Customers";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import ComingSoon from "@/pages/ComingSoon";
import Stock from "@/pages/Stock";
import StockCounts from "@/pages/StockCounts";
import StockCountDetail from "@/pages/StockCountDetail";
import StockTransfers from "@/pages/StockTransfers";
import SmartOrder from "@/pages/SmartOrder";
import CashflowForecast from "@/pages/CashflowForecast";
import BalanceCheck from "@/pages/BalanceCheck";
import BankRecon from "@/pages/BankRecon";
import CodeBatches from "@/pages/CodeBatches";
import CustomerCard from "@/pages/CustomerCard";
import QuickSale from "@/pages/QuickSale";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Collections from "@/pages/Collections";
import Instruments from "@/pages/Instruments";
import Promises from "@/pages/Promises";
import Upcoming from "@/pages/Upcoming";
import Finance from "@/pages/Finance";
import OrderPrint from "@/pages/OrderPrint";
import TemplatePrint from "@/pages/TemplatePrint";
import TemplateDesigner from "@/pages/TemplateDesigner";
import PurchaseReturnPrint from "@/pages/PurchaseReturnPrint";
import PrimReceipt from "@/pages/PrimReceipt";
import Depo from "@/pages/Depo";
import Sevkiyat from "@/pages/Sevkiyat";
import Purchasing from "@/pages/Purchasing";
import Purchases from "@/pages/Purchases";
import Backorders from "@/pages/Backorders";
import PurchaseEntry from "@/pages/PurchaseEntry";
import PurchaseDetail from "@/pages/PurchaseDetail";
import Imports from "@/pages/Imports";
import ImportDetail from "@/pages/ImportDetail";
import Prim from "@/pages/Prim";
import Reports from "@/pages/Reports";
import ImportReport from "@/pages/ImportReport";
import InsaatInstrumentReport from "@/pages/InsaatInstrumentReport";
import ProductSalesReport from "@/pages/ProductSalesReport";
import ProductDetail from "@/pages/ProductDetail";
import Virman from "@/pages/Virman";
import VirmanDetail from "@/pages/VirmanDetail";
import Construction from "@/pages/Construction";
import ConstructionDetail from "@/pages/ConstructionDetail";
import Takip from "@/pages/Takip";
import AiOzet from "@/pages/AiOzet";
import Zekat from "@/pages/Zekat";
import "@/lib/activity";

const PAGES = { dashboard: Dashboard, urunler: Products, cariler: Customers, kullanicilar: Users, ayarlar: Settings, stok: Stock, sayim: StockCounts, depo_transfer: StockTransfers, akilli_siparis: SmartOrder, nakit_akis: CashflowForecast, bakiye_kontrol: BalanceCheck, banka_mutabakat: BankRecon, kod_uretim: CodeBatches,
  hizli_satis: QuickSale, siparisler: Orders, tahsilatlar: Collections, cek_senet: Instruments, odeme_sozleri: Promises,
  yaklasan_tahsilat: Upcoming, finans: Finance, depo: Depo, sevkiyat: Sevkiyat, satin_alma: Purchasing, alislar: Purchases, bekleyen: Backorders, ithalat: Imports, prim: Prim, raporlar: Reports, virman: Virman, alis_iade: Virman, insaat: Construction, takip: Takip, ai_ozet: AiOzet, zekat: Zekat };
const ITHALAT = MODULES.find((m) => m.key === "ithalat");
const CARILER = MODULES.find((m) => m.key === "cariler");
const SIPARISLER = MODULES.find((m) => m.key === "siparisler");
const INSAAT = MODULES.find((m) => m.key === "insaat");
const ALISLAR = MODULES.find((m) => m.key === "alislar");

function RequireAuth({ children }) {
  const { user, bootError, reload, logout } = useAuth();
  if (user === null) return <FullScreenLoader error={bootError} onRetry={reload} onLogin={logout} />;
  if (user === false) return <Navigate to="/giris" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function HomeRedirect() {
  const { can } = useAuth();
  const first = MODULES.find((m) => canSee(can, m));
  return <Navigate to={first ? first.path : "/yetkisiz"} replace />;
}

function ModuleGate({ module, page }) {
  const { can } = useAuth();
  if (!canSee(can, module)) return <Forbidden />;
  const Page = page || PAGES[module.key];
  return Page ? <Page /> : <ComingSoon module={module} />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route path="/gizlilik" element={<Privacy lang="tr" />} />
          <Route path="/privacy" element={<Privacy lang="en" />} />
          <Route path="/hesap-silme" element={<AccountDeletion />} />
          <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route index element={<HomeRedirect />} />
            <Route path="yetkisiz" element={<Forbidden />} />
            {MODULES.map((m) => <Route key={m.key} path={m.path.slice(1)} element={<ModuleGate module={m} />} />)}
            <Route path="cariler/:id" element={<ModuleGate module={CARILER} page={CustomerCard} />} />
            <Route path="siparisler/:id" element={<ModuleGate module={SIPARISLER} page={OrderDetail} />} />
            <Route path="alislar/yeni" element={<ModuleGate module={ALISLAR} page={PurchaseEntry} />} />
            <Route path="alislar/:id" element={<ModuleGate module={ALISLAR} page={PurchaseDetail} />} />
            <Route path="insaat/:id" element={<ModuleGate module={INSAAT} page={ConstructionDetail} />} />
            <Route path="ithalat/:id" element={<ModuleGate module={ITHALAT} page={ImportDetail} />} />
            <Route path="sayim/:id" element={<ModuleGate module={MODULES.find((m) => m.key === "sayim")} page={StockCountDetail} />} />
            <Route path="raporlar/insaat-cek-senet" element={<ModuleGate module={MODULES.find((m) => m.key === "insaat")} page={InsaatInstrumentReport} />} />
            <Route path="raporlar/ithalat" element={<ModuleGate module={ITHALAT} page={ImportReport} />} />
            <Route path="urunler/:id" element={<ModuleGate module={MODULES.find((m) => m.key === "urunler")} page={ProductDetail} />} />
            <Route path="raporlar/urun-satis" element={<ModuleGate module={MODULES.find((m) => m.key === "raporlar")} page={ProductSalesReport} />} />
          </Route>
          <Route path="/siparisler/:id/yazdir/t/:tid" element={<RequireAuth><TemplatePrint /></RequireAuth>} />
          <Route path="/siparisler/:id/yazdir/:doc" element={<RequireAuth><OrderPrint /></RequireAuth>} />
          <Route path="/ayarlar/sablonlar/:tid" element={<RequireAuth><TemplateDesigner /></RequireAuth>} />
          <Route path="/alis-iadeleri/:id/yazdir" element={<RequireAuth><PurchaseReturnPrint /></RequireAuth>} />
          <Route path="/purchase-returns/:id/print" element={<RequireAuth><PurchaseReturnPrint /></RequireAuth>} />
          <Route path="/virman/:id" element={<RequireAuth><VirmanDetail /></RequireAuth>} />
          <Route path="/prim/makbuz/:id" element={<RequireAuth><PrimReceipt /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
      <ConnectionBanner />
    </BrowserRouter>
  );
}

export default App;
